
export const cognitive = {
  intro: {
    title: 'Cognitive Assessment',
    subtitle: 'Measure your cognitive ability through verbal, numerical, and abstract reasoning.',
    duration: '{{timeLimit}} minutes · 50 questions',
    areas: 'Verbal, numerical, and abstract reasoning',
    beforeYouBegin: 'Before you begin',
    instruction1: 'Find a quiet environment with no distractions',
    instruction2: 'Ensure you have {{timeLimit}} uninterrupted minutes',
    instruction3: 'The timer cannot be paused once started',
    instruction4: 'Work quickly but accurately — no penalty for wrong answers',
    startAssessment: 'Start Assessment',
    backToHome: 'Back to home',
    aiWarningTitle: 'Academic Integrity Notice',
    aiWarningText: 'Use of AI tools, external assistance, or any unauthorized resources is strictly prohibited. Our system actively monitors for irregular patterns. Candidates found using AI or external help will be immediately disqualified.'
  },
  loading: {
    title: 'Loading Assessment...',
    message: 'Please wait while we prepare your cognitive assessment.'
  },
  error: {
    title: 'Assessment Error',
    message: 'There was an issue loading the questions. Please try again.',
    retry: 'Retry Assessment',
    indexTitle: 'Question Index Error',
    indexMessage: 'Question is out of range. Please restart the assessment.',
    questionTitle: 'Question Error',
    questionMessage: 'There was an issue with this question. Please try again.',
    restart: 'Restart Assessment'
  },
  test: {
    questionCounter: 'Question',
    of: 'of',
    reasoning: 'Reasoning'
  },
  types: {
    verbal: 'Verbal',
    numerical: 'Numerical',
    abstract: 'Abstract'
  },
  results: {
    title: 'Your Cognitive Assessment Results',
    subtitle: 'Comprehensive analysis of your cognitive abilities',
    yourStrengths: 'Your Cognitive Strengths',
    strongestArea: 'Your strongest cognitive area',
    percentile: 'th percentile',
    performance: 'Performance Summary',
    questionsCompleted: 'Questions Completed',
    correctResponses: 'Correct Responses',
    successRate: 'Success Rate',
    timeManagement: 'Time Management',
    remaining: 'remaining',
    growthOpportunities: 'Growth Opportunities',
    understanding: 'Understanding Your Results',
    profileDesc: 'Your cognitive profile shows how you process different types of information and solve problems across three key areas.',
    verbalReasoning: 'Verbal Reasoning',
    verbalDesc: 'Your ability to understand, analyze, and work with written information and language concepts.',
    numericalReasoning: 'Numerical Reasoning',
    numericalDesc: 'Your capacity to work with numbers, mathematical concepts, and quantitative relationships.',
    abstractReasoning: 'Abstract Reasoning',
    abstractDesc: 'Your skill in identifying patterns, logical relationships, and solving novel problems.',
    reminder: 'Remember: Cognitive abilities can be developed through practice and learning.',
    practiceTip: 'Practice Regularly',
    practiceDesc: 'Regular mental exercises and challenging puzzles can help improve cognitive performance.',
    learningPath: 'Focused Learning',
    learningDesc: 'Target your weaker areas with specific training programs and educational resources.',
    nextSteps: 'Next Steps',
    nextStepsDesc: 'Consider retaking the assessment after focused practice to track your improvement.'
  },
  performance: {
    outstanding: 'Outstanding Performance',
    strong: 'Strong Performance',
    good: 'Good Performance',
    developing: 'Developing Performance',
    growing: 'Growing Performance',
    learning: 'Learning Performance'
  },
  message: {
    outstanding: 'Exceptional cognitive abilities! You demonstrate superior reasoning skills across multiple domains.',
    strong: 'Strong cognitive performance! You show well-developed reasoning abilities with room for continued growth.',
    good: 'Solid cognitive abilities! You demonstrate good reasoning skills with potential for further development.',
    developing: 'Your cognitive abilities are developing well. With focused practice, you can strengthen your reasoning skills.',
    growing: 'You show promise in cognitive reasoning. Continued learning and practice will help you improve.',
    learning: 'Every assessment is a learning opportunity. Focus on building your cognitive skills step by step.'
  },
  thankYou: {
    title: 'Assessment Complete!',
    message: 'Thank you for completing the assessment.',
    backToHome: 'Return to Home'
  },
  questions: {
    verbal: {
      q1: {
        question: 'Which word best completes this analogy: BOOK is to read as SONG is to ____',
        optionA: 'Write',
        optionB: 'Listen',
        optionC: 'Dance',
        optionD: 'Sing'
      },
      q2: {
        question: 'What is the antonym of "MUNDANE"?',
        optionA: 'Ordinary',
        optionB: 'Exciting',
        optionC: 'Daily',
        optionD: 'Simple'
      },
      q3: {
        question: 'Which word does NOT belong with the others?',
        optionA: 'Elated',
        optionB: 'Jubilant',
        optionC: 'Ecstatic',
        optionD: 'Melancholy'
      },
      q4: {
        question: '"ARTICULATE" is to speaking as "GRACEFUL" is to ____',
        optionA: 'Thinking',
        optionB: 'Moving',
        optionC: 'Writing',
        optionD: 'Singing'
      },
      q5: {
        question: 'Which word is closest in meaning to "PLAUSIBLE"?',
        optionA: 'Impossible',
        optionB: 'Believable',
        optionC: 'Strange',
        optionD: 'Certain'
      },
      q6: {
        question: 'Which word best describes someone who is "PRAGMATIC"?',
        optionA: 'Idealistic',
        optionB: 'Practical',
        optionC: 'Emotional',
        optionD: 'Artistic'
      },
      q7: {
        question: 'What is the meaning of "EPHEMERAL"?',
        optionA: 'Permanent',
        optionB: 'Short-lived',
        optionC: 'Large',
        optionD: 'Beautiful'
      },
      q8: {
        question: 'Which word is most similar to "TENACIOUS"?',
        optionA: 'Weak',
        optionB: 'Persistent',
        optionC: 'Quick',
        optionD: 'Gentle'
      },
      q9: {
        question: 'What does "CANDID" mean?',
        optionA: 'Sweet',
        optionB: 'Hidden',
        optionC: 'Honest',
        optionD: 'Angry'
      },
      q10: {
        question: '"SOPHISTICATED" is to simple as "COMPLEX" is to ____',
        optionA: 'Difficult',
        optionB: 'Easy',
        optionC: 'Basic',
        optionD: 'Advanced'
      },
      q11: {
        question: 'What is the best synonym for "UBIQUITOUS"?',
        optionA: 'Rare',
        optionB: 'Everywhere',
        optionC: 'Hidden',
        optionD: 'New'
      },
      q12: {
        question: 'What is the most precise synonym for "PERSPICACIOUS"?',
        optionA: 'Confused',
        optionB: 'Insightful',
        optionC: 'Loud',
        optionD: 'Mysterious'
      },
      q13: {
        question: 'Which word best completes: "INDIGENOUS" is to native as "EXTRANEOUS" is to ____',
        optionA: 'Essential',
        optionB: 'Irrelevant',
        optionC: 'Internal',
        optionD: 'Obvious'
      },
      q14: {
        question: 'In a logical argument, if all roses are flowers and all flowers need water, what can we conclude about roses?',
        optionA: 'They are beautiful',
        optionB: 'They need water',
        optionC: 'They are red',
        optionD: 'They have thorns'
      },
      q15: {
        question: 'Which word is most similar in meaning to "CONUNDRUM"?',
        optionA: 'Solution',
        optionB: 'Puzzle',
        optionC: 'Answer',
        optionD: 'Statement'
      },
      q16: {
        question: 'What does "SURREPTITIOUS" mean?',
        optionA: 'Open',
        optionB: 'Secret',
        optionC: 'Loud',
        optionD: 'Clear'
      },
      q17: {
        question: 'Complete the analogy: VINDICATE is to justify as CASTIGATE is to ____',
        optionA: 'Praise',
        optionB: 'Criticize',
        optionC: 'Ignore',
        optionD: 'Support'
      }
    },
    numerical: {
      q18: {
        question: 'If a product costs $80 and is marked down 25%, what is the new price?',
        optionA: '$55',
        optionB: '$60',
        optionC: '$65',
        optionD: '$70'
      },
      q19: {
        question: 'What comes next in this sequence: 2, 6, 18, 54, ?',
        optionA: '108',
        optionB: '162',
        optionC: '216',
        optionD: '270'
      },
      q20: {
        question: 'If 3x + 7 = 22, what is x?',
        optionA: '3',
        optionB: '4',
        optionC: '5',
        optionD: '6'
      },
      q21: {
        question: 'What is the next number: 1, 4, 9, 16, 25, ?',
        optionA: '30',
        optionB: '32',
        optionC: '36',
        optionD: '40'
      },
      q22: {
        question: 'If 40% of a number is 60, what is the number?',
        optionA: '120',
        optionB: '140',
        optionC: '150',
        optionD: '160'
      },
      q23: {
        question: 'What is 25% of 80 plus 30% of 60?',
        optionA: '35',
        optionB: '38',
        optionC: '41',
        optionD: '44'
      },
      q24: {
        question: 'If y = 2x + 3 and x = 4, what is y?',
        optionA: '9',
        optionB: '10',
        optionC: '11',
        optionD: '12'
      },
      q25: {
        question: 'What is the pattern: 2, 5, 11, 23, ?',
        optionA: '41',
        optionB: '45',
        optionC: '47',
        optionD: '51'
      },
      q26: {
        question: 'If a box contains 24 items and you remove 1/3, how many remain?',
        optionA: '14',
        optionB: '16',
        optionC: '18',
        optionD: '20'
      },
      q27: {
        question: 'What is the next number in this sequence: 1, 8, 27, 64, ?',
        optionA: '100',
        optionB: '125',
        optionC: '144',
        optionD: '169'
      },
      q28: {
        question: 'If 2x - 5 = 3x + 7, what is x?',
        optionA: '-12',
        optionB: '-10',
        optionC: '-8',
        optionD: '-6'
      },
      q29: {
        question: 'A sequence follows the rule: each term is double the previous plus 1. If the first term is 3, what is the fourth term?',
        optionA: '15',
        optionB: '21',
        optionC: '31',
        optionD: '35'
      },
      q30: {
        question: 'What percentage is 45 out of 180?',
        optionA: '20%',
        optionB: '25%',
        optionC: '30%',
        optionD: '35%'
      },
      q31: {
        question: 'If the average of 5 numbers is 12, and 4 of them are 8, 10, 14, and 16, what is the fifth number?',
        optionA: '10',
        optionB: '12',
        optionC: '14',
        optionD: '16'
      },
      q32: {
        question: 'A car travels 240 km in 3 hours, then 160 km in 2 hours. What is the average speed for the entire journey?',
        optionA: '75 km/h',
        optionB: '80 km/h',
        optionC: '85 km/h',
        optionD: '90 km/h'
      },
      q33: {
        question: 'If 3/4 of a number equals 2/3 of 90, what is the number?',
        optionA: '72',
        optionB: '75',
        optionC: '80',
        optionD: '85'
      },
      q34: {
        question: 'What is the next term in the sequence: 3, 7, 15, 31, ?',
        optionA: '59',
        optionB: '63',
        optionC: '67',
        optionD: '71'
      }
    },
    abstract: {
      q35: {
        question: 'Complete the pattern: A1B2C3D4?',
        optionA: 'E5',
        optionB: 'F6',
        optionC: 'E4',
        optionD: 'D5'
      },
      q36: {
        question: 'Which shape is the odd one out: Circle, Square, Triangle, Rectangle?',
        optionA: 'Circle',
        optionB: 'Square',
        optionC: 'Triangle',
        optionD: 'Rectangle'
      },
      q37: {
        question: 'What comes next: 1, 1, 2, 3, 5, 8, ?',
        optionA: '11',
        optionB: '12',
        optionC: '13',
        optionD: '14'
      },
      q38: {
        question: 'If ★ represents 4 and ♦ represents 7, what is ★ × ♦?',
        optionA: '24',
        optionB: '26',
        optionC: '28',
        optionD: '30'
      },
      q39: {
        question: 'Which number completes the pattern: 2, 4, 8, 16, ?',
        optionA: '24',
        optionB: '28',
        optionC: '32',
        optionD: '36'
      },
      q40: {
        question: 'What pattern continues: XYZ, WXY, VWX, ?',
        optionA: 'UVW',
        optionB: 'VWX',
        optionC: 'TUV',
        optionD: 'UVX'
      },
      q41: {
        question: 'Complete the sequence: 100, 81, 64, 49, ?',
        optionA: '36',
        optionB: '35',
        optionC: '34',
        optionD: '33'
      },
      q42: {
        question: 'Which comes next: ◐◑◒◓?',
        optionA: '◐',
        optionB: '◑',
        optionC: '◒',
        optionD: '◓'
      },
      q43: {
        question: 'If patterns repeat every 3: ABCABCABC, what is the 10th element?',
        optionA: 'A',
        optionB: 'B',
        optionC: 'C',
        optionD: 'D'
      },
      q44: {
        question: 'Complete: 1A, 3B, 5C, 7D, ?',
        optionA: '8E',
        optionB: '9E',
        optionC: '9F',
        optionD: '10E'
      },
      q45: {
        question: 'Which element breaks the pattern: AEIOUA',
        optionA: 'A',
        optionB: 'E',
        optionC: 'I',
        optionD: 'U'
      },
      q46: {
        question: 'What is the next number in: 2, 6, 12, 20, 30, ?',
        optionA: '40',
        optionB: '42',
        optionC: '44',
        optionD: '46'
      },
      q47: {
        question: 'Complete the logical sequence: Z1A, Y2B, X3C, ?',
        optionA: 'W4D',
        optionB: 'V4D',
        optionC: 'W5D',
        optionD: 'V5E'
      },
      q48: {
        question: 'If symbols follow a pattern: ♠=1, ♥=4, ♦=9, ♣=16, what does ★ equal?',
        optionA: '20',
        optionB: '25',
        optionC: '30',
        optionD: '36'
      },
      q49: {
        question: 'What comes next in the matrix pattern: [1,2][3,4], [2,3][4,5], [3,4][5,6], ?',
        optionA: '[4,5][6,7]',
        optionB: '[4,6][6,8]',
        optionC: '[5,6][7,8]',
        optionD: '[3,5][5,7]'
      },
      q50: {
        question: 'Complete the transformation: ABC→BCD, DEF→EFG, HIJ→?',
        optionA: 'IJK',
        optionB: 'JKL',
        optionC: 'HIK',
        optionD: 'HJK'
      }
    }
  }
};
